import { useEffect, useMemo, useRef, useState } from 'react';
import { useApiClient } from '../../../hooks/useRestAPIHooks';
import {
  Folder,
  ModelUtils,
  rootFolder,
  TemplatingItemSummary,
} from '../models';
import { getTemplatingItemsInFolder } from './getTemplatingItemsInFolder';

export const useAllTemplatingItemSummaries = ({
  enabled = true,
  folder = rootFolder,
  refetchTrigger,
}: {
  enabled?: boolean;
  folder?: Folder;
  refetchTrigger?: unknown;
} = {}) => {
  const apiClient = useApiClient();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;
  const normalizedFolder = useMemo(
    () => ModelUtils.createFolder(folder.path),
    [folder.path],
  );
  const [items, setItems] = useState<TemplatingItemSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setIsLoading(false);
      return;
    }
    let isCancelled = false;
    setIsLoading(true);

    void getTemplatingItemsInFolder(apiClientRef.current, normalizedFolder)
      .then(templatingItems => {
        if (isCancelled) {
          return;
        }
        const uniqueTemplatingItems = new Map<string, TemplatingItemSummary>();
        for (const templatingItem of templatingItems) {
          uniqueTemplatingItems.set(templatingItem.id, templatingItem);
        }
        setItems([...uniqueTemplatingItems.values()]);
      })
      .catch(() => {
        if (!isCancelled) {
          setItems([]);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [enabled, normalizedFolder, refetchTrigger]);

  return {
    items,
    isLoading,
  };
};
